import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import * as Yup from 'yup';

import api from '~/services/api';
import toast from '~/services/toast';
import { APP_ERROR, API_BASE_URL, API_NODE_URL, APP_SETTINGS } from '~/constants';
import * as actions from '~/store/modules/pdv/pdv-actions';
import ModalMapaEndereco from '../ModalMapaEndereco';
import { LAT_MIN_BRASIL, LAT_MAX_BRASIL, LNG_MIN_BRASIL, LNG_MAX_BRASIL } from '../ModalMapaEndereco/geoLocalizacao.constants';

const coordenadaValida = valor =>
  valor !== null && valor !== undefined && String(valor).trim() !== '' && !Number.isNaN(Number(valor));

const cacheCoordenadas = (lat, lng) => {
  sessionStorage.setItem('latlng', JSON.stringify({ lat, lng }));
};

const getCachedCoordenadas = () => {
  const stringCoords = sessionStorage.getItem('latlng');
  if (stringCoords) return JSON.parse(stringCoords);
  return null;
};

const getCoordenadasEndereco = async endereco => {
  const { data: enderecos } = await api.post(`${API_NODE_URL}/consulta/buscar-endereco`, {
    endereco
  });

  if (!Array.isArray(enderecos) || !enderecos.length) return null;

  const { placeId } = enderecos[0];
  const { data: coords } = await api.get(`${API_NODE_URL}/consulta/geocodificacao/${placeId}`);

  return coords;
};

export const ModalCadastroEnderecoCliente = ({
  data,
  clienteId = null,
  showModal,
  onClose,
  callback
}) => {
  const temaAtual = JSON.parse(sessionStorage.getItem(APP_SETTINGS));
  const dispatch = useDispatch();
  const { venda } = useSelector(state => state.pdv);
  const { usuario, empresa } = useSelector(state => state.auth ?? {});

  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingCidade, setLoadingCidade] = useState(false);
  const [loadingSaving, setLoadingSaving] = useState(false);
  const [intClienteId] = useState(clienteId ?? venda?.intCliente?.id);

  const [endereco, setEndereco] = useState({
    geoLatitude: null,
    geoLongitude: null,
    geoDataHora: null,
    geoOrigem: null,
    geoUsuarioId: null,
    ...data
  });
  const [cidades, setCidades] = useState([]);
  const [showModalMapa, setShowModalMapa] = useState(false);
  const [obtendoLocalizacao, setObtendoLocalizacao] = useState(true);
  const [localizacaoAtual, setLocalizacaoAtual] = useState(null);
  const cidadeSearchTimeout = useRef(null);

  useEffect(() => {
    if (endereco.cep) {
      const cep = endereco.cep.replace(/[^0-9]/g, '').slice(0, 8);
      if (cep !== endereco.cep) {
        setEndereco(prev => ({ ...prev, cep }));
      }
    }
  }, [endereco.cep]);

  useEffect(() => {
    const cachedCoordenadas = getCachedCoordenadas();
    if (cachedCoordenadas) {
      setLocalizacaoAtual(cachedCoordenadas);
      setObtendoLocalizacao(false);
      return;
    }

    if (!navigator.geolocation) {
      setObtendoLocalizacao(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude: lat, longitude: lng } = position.coords;
        cacheCoordenadas(lat, lng);
        setLocalizacaoAtual({ lat, lng });
        setObtendoLocalizacao(false);
      },
      () => {
        if (!empresa?.enderecoIntCidade || !empresa?.uf) {
          setObtendoLocalizacao(false);
          return;
        }

        getCoordenadasEndereco(`${empresa.enderecoIntCidade} - ${empresa.uf}`)
          .then(coords => {
            if (coordenadaValida(coords?.lat) && coordenadaValida(coords?.lng)) {
              setLocalizacaoAtual({ lat: coords.lat, lng: coords.lng });
            }
          })
          .catch(() => {})
          .finally(() => setObtendoLocalizacao(false));
      },
      { timeout: 10000 }
    );
  }, []);

  useEffect(() => {
    return () => {
      if (cidadeSearchTimeout.current) {
        clearTimeout(cidadeSearchTimeout.current);
      }
    };
  }, []);

  const getLabelBtnAbrirMapa = values => {
    if (values?.geoDataHora) return 'Localização confirmada';
    if (obtendoLocalizacao) return 'Obtendo localização...';
    if (coordenadaValida(values?.geoLatitude) && coordenadaValida(values?.geoLongitude))
      return 'Confirmar localização';
    return 'Localizar no Mapa';
  };

  const geoPreenchido = value => value !== undefined && value !== null && value !== '';

  const [validationSchema] = useState(
    Yup.object().shape(
      {
        descricao: intClienteId
          ? Yup.string().required('Descrição é obrigatório')
          : Yup.string().optional(),

        cep: Yup.string().required('CEP é obrigatório'),
        logradouro: Yup.string().required('Logradouro é obrigatório'),
        logradouroNumero: Yup.string()
          .matches(/^\d+$/, 'Apenas números são permitidos')
          .required('Número é obrigatório'),
        bairro: Yup.string().required('Bairro é obrigatório'),
        intCidade: Yup.object().nullable().required('Cidade é obrigatório'),
        geoLatitude: Yup.number()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError('Latitude inválida')
          .min(LAT_MIN_BRASIL, `Latitude deve estar dentro do território brasileiro (${LAT_MIN_BRASIL} a ${LAT_MAX_BRASIL})`)
          .max(LAT_MAX_BRASIL, `Latitude deve estar dentro do território brasileiro (${LAT_MIN_BRASIL} a ${LAT_MAX_BRASIL})`)
          .nullable()
          .when('geoLongitude', {
            is: geoPreenchido,
            then: schema => schema.required('Latitude é obrigatória quando a longitude é informada')
          }),
        geoLongitude: Yup.number()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError('Longitude inválida')
          .min(LNG_MIN_BRASIL, `Longitude deve estar dentro do território brasileiro (${LNG_MIN_BRASIL} a ${LNG_MAX_BRASIL})`)
          .max(LNG_MAX_BRASIL, `Longitude deve estar dentro do território brasileiro (${LNG_MIN_BRASIL} a ${LNG_MAX_BRASIL})`)
          .nullable()
          .when('geoLatitude', {
            is: geoPreenchido,
            then: schema => schema.required('Longitude é obrigatória quando a latitude é informada')
          })
      },
      [['geoLatitude', 'geoLongitude']]
    )
  );

  const extrairTermoBuscaCidade = value => {
    const match = value?.match(/^(\d+)\s*-\s*(.+)\/([A-Za-z]{2})$/);
    return {
      codigo: match ? match[1] : value,
      nome: (match ? match[2] : value)?.toUpperCase()
    };
  };

  const filterOptionsCidade = (options, { inputValue }) => {
    if (!inputValue) {
      return options;
    }
    const { codigo, nome } = extrairTermoBuscaCidade(inputValue);
    return options.filter(
      option =>
        option?.codigoIbge?.toString().toUpperCase().includes(codigo?.toUpperCase()) ||
        option?.cidade?.toUpperCase().includes(nome)
    );
  };

  const handleChangeCidade = async value => {
    if (!value) {
      return;
    }

    if (loadingCidade) {
      return;
    }

    try {
      setLoadingCidade(true);

      const { codigo, nome } = extrairTermoBuscaCidade(value);

      const $select = ['id', 'uf', 'cidade', 'codigoIbge'].join(',');
      const $filter = [
        `contains(codigoIbge,'${codigo?.toUpperCase()}')`,
        `contains(cidade,'${nome}')`
      ].join(' or ');

      const { data } = await api.get(
        `${API_BASE_URL}/v1/integracao/cidade/?$select=${$select?.toString()}&$filter=${$filter?.toString()}`
      );

      setCidades([...data]);
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      setLoadingCidade(false);
    }
  };

  const handleChangeCEP = async values => {
    let cep = values.cep.replace(/[^0-9]/g, '');
    cep = cep.slice(0, 8);

    if (cep?.length !== 8) {
      return;
    }

    if (loadingCep) {
      return;
    }

    try {
      setLoadingCep(true);

      let consultaCep = null;

      try {
        const { data } = await api.get(`https://viacep.com.br/ws/${cep}/json/`, {
          transformRequest: [
            (config, headers) => {
              delete headers.Authorization;

              return config;
            }
          ]
        });

        consultaCep = data;
      } catch (viacepError) {
        const { data } = await api.get(`https://brasilapi.com.br/api/cep/v1/${cep}`, {
          transformRequest: [
            (config, headers) => {
              delete headers.Authorization;

              return config;
            }
          ]
        });

        consultaCep = {
          cep: data.cep,
          logradouro: data.street,
          bairro: data.neighborhood,
          localidade: data.city,
          uf: data.state
        };
      }

      const $select = ['id', 'uf', 'cidade', 'codigoIbge'].join(',');

      if (!consultaCep?.erro) {
        const $filter = [
          `contains(codigoIbge,'${consultaCep?.ibge}')`,
          `contains(cidade,'${consultaCep?.localidade?.toUpperCase()}')`
        ].join(' or ');

        const { data } = await api.get(
          `${API_BASE_URL}/v1/integracao/cidade/?$select=${$select?.toString()}&$filter=${$filter?.toString()}`
        );

        const cidade = data?.[0];

        setCidades(cidade ? [cidade] : []);
        setEndereco({
          ...values,
          cep: values.cep,
          bairro: consultaCep?.bairro,
          logradouro: consultaCep?.logradouro,
          uf: consultaCep?.uf,
          intCidade: cidade ? { ...cidade } : values?.intCidade,
          intCidadeId: cidade?.id ?? values?.intCidadeId
        });
      } else {
        const { data: _cidades } = await api.get(
          `${API_BASE_URL}/v1/integracao/cidade/?$select=${$select?.toString()}`
        );

        setCidades([..._cidades]);
        toast.error('Nenhum endereço foi encontrado para o CEP informado');
        setEndereco({
          ...values,
          cep: values.cep
        });
      }
    } catch (error) {
      toast.error('Ocorreu um erro ao buscar o CEP. Preencha o endereço manualmente.');
      dispatch({ type: APP_ERROR, error });
    } finally {
      setLoadingCep(false);
    }
  };

  const handleSubmit = async values => {
    setLoadingSaving(true);
    try {
      if(values.bairro) values.bairro = values.bairro.replace(/\s+$/, '');
      if(values.cep) values.cep = values.cep.replace(/\s+$/, '');
      if(values.descricao) values.descricao = values.descricao.replace(/\s+$/, '');
      if(values.logradouro) values.logradouro = values.logradouro.replace(/\s+$/, '');
      if(values.logradouroNumero) values.logradouroNumero = values.logradouroNumero.replace(/\s+$/, '');
      if(values.referencia) values.referencia = values.referencia.replace(/\s+$/, '');
      if (!values.uf && values.intCidade?.uf) values.uf = values.intCidade.uf;

      if (values.cep.length !== 8) {
        toast.error('Formato de CEP inválido');
        setLoadingSaving(false);
        return;
      }

      values.cep = values.cep.replace(/^(\d{2})(\d{3})(\d{3})$/, '$1.$2-$3');

      if (values.descricao && values.descricao.length < 3) {
        toast.error('Campo descrição precisa ter no mínimo 3 caracteres');
        setLoadingSaving(false);
        return;
      }

      const geoLatitude = coordenadaValida(values.geoLatitude) ? Number(values.geoLatitude) : null;
      const geoLongitude = coordenadaValida(values.geoLongitude) ? Number(values.geoLongitude) : null;
      const geoValida = coordenadaValida(geoLatitude) && coordenadaValida(geoLongitude);
      const geoAindaNaoConfirmado = !values.geoDataHora;

      const geoOrigem =
        geoValida && geoAindaNaoConfirmado ? 'cadastro_cliente_pdv' : values.geoOrigem ?? null;
      const geoUsuarioId =
        geoValida && geoAindaNaoConfirmado ? usuario?.id ?? null : values.geoUsuarioId ?? null;
      const geoDataHora =
        geoValida && geoAindaNaoConfirmado ? new Date() : values.geoDataHora ?? null;

      values = { ...values, geoLatitude, geoLongitude, geoOrigem, geoUsuarioId, geoDataHora };

      if (intClienteId == null && values?.id == null) {
        if (callback) callback(values);
        setLoadingSaving(false);
        return;
      }

      const intCidadeId = Array.isArray(values?.intCidade)
        ? values?.intCidade?.[0]?.id
        : values?.intCidade?.id;

      const { data } =
        endereco?.id > 0
          ? await api.put(
              `${API_NODE_URL}/integracao/cliente/${venda?.intCliente?.id}/enderecos/${endereco?.id}`,
              {
                ...values,
                intCidadeId: intCidadeId ?? null
              }
            )
          : await api.post(
              `${API_NODE_URL}/integracao/cliente/${venda?.intCliente?.id}/enderecos`,
              {
                ...values,
                intClienteId: venda?.intCliente?.id ?? null,
                intCidadeId: intCidadeId ?? null
              }
            );

      if (callback) callback({ ...data, ...values });
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      setLoadingSaving(false);
    }
  };

  return (
    <>
      <Mui.Dialog open={showModal} onClose={onClose} PaperComponent={Mui.Paper} maxWidth={'lg'}>
        <Mui.DialogTitle>
          <Mui.AppBar color="primary" position="static">
            <Mui.Toolbar>
              <Mui.Box style={{ minWidth: 180 }}>
                <Mui.Typography variant="h5" style={{ textTransform: 'upperCase' }}>
                  Cadastrar endereço
                </Mui.Typography>
              </Mui.Box>
              <Mui.Box flexGrow={1} />

              <Mui.IconButton color="inherit" onClick={onClose} aria-label="close">
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Toolbar>
          </Mui.AppBar>
        </Mui.DialogTitle>

        <Mui.DialogContent style={{ padding: 16, width: 880 }}>
          <Formik
            initialValues={endereco}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              setFieldTouched,
              touched,
              values
            }) => {
              return (
              <>
                <form onSubmit={handleSubmit} autoComplete="randomStringToTrickBrowsersThatIgnoreOff">
                  <Mui.Grid container spacing={4} style={{ padding: 16 }}>
                      <Mui.Grid item md={4}>
                        <Mui.TextField
                          name="cep"
                          label={'CEP'}
                          value={values?.cep ?? ''}
                          error={Boolean(touched.cep && errors.cep)}
                          fullWidth
                          required
                          helperText={touched.cep && errors.cep}
                          placeholder={`00000000`}
                          onBlur={handleBlur}
                          onChange={e => {
                            let _cep = e.target.value.replace(/[^0-9]/g, '');
                            _cep = _cep.slice(0, 8);
                            setFieldValue('cep', _cep);
                            if (_cep !== values?.cep) {
                              handleChangeCEP({ ...values, cep: _cep });
                            }
                          }}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            }
                          }}
                          InputProps={{
                            endAdornment: loadingCep ? (
                              <Mui.InputAdornment position="end">
                                <Mui.CircularProgress color="inherit" size={20} />
                              </Mui.InputAdornment>
                            ) : null
                          }}
                          inputProps={{
                            maxLength: 16,
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            }
                          }}
                        />
                      </Mui.Grid>
                      {(
                          <Mui.Grid item md={8}>
                            <Mui.TextField
                              name="descricao"
                              label={'Descrição'}
                              value={values?.descricao ?? ''}
                              error={Boolean(touched.descricao && errors.descricao)}
                              fullWidth
                              required
                              helperText={touched.descricao && errors.descricao}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              variant="outlined"
                              disabled={loadingSaving || loadingCep || loadingCidade}
                              InputLabelProps={{ 
                                shrink: true,
                                style: {
                                  color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                                  fontWeight: 'bold',
                                } 
                              }}
                              inputProps={{
                                maxLength: 40,
                                autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                                style: {
                                  color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                                },
                              }}
                            />
                          </Mui.Grid>
                        )}
                      <Mui.Grid item md={8}>
                        <Mui.TextField
                          name="logradouro"
                          label={'Logradouro'}
                          value={values?.logradouro ?? ''}
                          error={Boolean(touched.logradouro && errors.logradouro)}
                          fullWidth
                          required
                          helperText={touched.logradouro && errors.logradouro}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{ 
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            } 
                          }}
                          inputProps={{
                            maxLength: 50,
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>
                      <Mui.Grid item md={4}>
                        <Mui.TextField
                          name="logradouroNumero"
                          label={'Número'}
                          value={values?.logradouroNumero ?? ''}
                          error={Boolean(touched.logradouroNumero && errors.logradouroNumero)}
                          fullWidth
                          required
                          helperText={touched.logradouroNumero && errors.logradouroNumero}
                          onBlur={handleBlur}
                          onChange={e => {
                            setFieldValue('logradouroNumero', e.target.value.replace(/[^0-9]/g, ''));
                          }}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            }
                          }}
                          inputProps={{
                            maxLength: 6,
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>

                      <Mui.Grid item md={12}>
                        <Mui.TextField
                          name="bairro"
                          label={'Bairro'}
                          value={values?.bairro ?? ''}
                          error={Boolean(touched.bairro && errors.bairro)}
                          fullWidth
                          required
                          helperText={touched.bairro && errors.bairro}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{ 
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            } 
                          }}
                          inputProps={{
                            maxLength: 30,
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>

                      <Mui.Grid item md={12}>
                        <Autocomplete
                          id="cidade-autocomplete"
                          getOptionSelected={(option, value) => option.id === value.id}
                          getOptionLabel={option =>
                            `${option?.codigoIbge} - ${option?.cidade}/${option?.uf}`
                          }
                          filterOptions={filterOptionsCidade}
                          options={cidades}
                          value={values?.intCidade ?? null}
                          loading={loadingCidade}
                          disabled={loadingSaving || loadingCep}
                          loadingText="Carregando..."
                          noOptionsText="Nenhum registro encontrado"
                          onInputChange={(_, value, reason) => {
                            if (reason !== 'input') {
                              return;
                            }
                            if (cidadeSearchTimeout.current) {
                              clearTimeout(cidadeSearchTimeout.current);
                            }
                            cidadeSearchTimeout.current = setTimeout(() => {
                              handleChangeCidade(value);
                            }, 500);
                          }}
                          onChange={(event, value) => {
                            event.persist();

                            setFieldValue('intCidade', value);
                            setFieldValue('intCidadeId', value?.id);
                            setFieldValue('uf', value?.uf ?? null);
                            setFieldTouched('intCidade', false, false);
                          }}
                          onBlur={() => {
                            setFieldTouched('intCidade', true, true);
                          }}
                          renderInput={params => (
                              <Mui.TextField
                                {...params}
                                label={'Cidade'}
                                placeholder={'Consultar....'}
                                error={Boolean(touched.intCidade && errors.intCidade)}
                                helperText={touched.intCidade && errors.intCidade}
                                fullWidth
                                disabled={loadingSaving || loadingCep}
                                variant="outlined"
                                InputLabelProps={{
                                  required: true,
                                  shrink: true,
                                  style: {
                                    color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                                    fontWeight: 'bold',
                                  }
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {loadingCidade ? (
                                        <Mui.CircularProgress color="inherit" size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  )
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                                  style: {
                                    color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                                  }
                                }}
                              />
                          )}
                        />
                      </Mui.Grid>

                      <Mui.Grid item md={12}>
                        <Mui.TextField
                          name="referencia"
                          label={'Referência'}
                          value={values?.referencia ?? ''}
                          error={Boolean(touched.referencia && errors.referencia)}
                          fullWidth
                          helperText={touched.referencia && errors.referencia}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{ 
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            } 
                          }}
                          inputProps={{
                            maxLength: 300,
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>

                      <Mui.Grid item md={4}>
                        <Mui.TextField
                          name="geoLatitude"
                          label={'Latitude'}
                          value={values?.geoLatitude ?? ''}
                          error={Boolean(touched.geoLatitude && errors.geoLatitude)}
                          helperText={touched.geoLatitude && errors.geoLatitude}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={e => {
                            setFieldValue('geoLatitude', e.target.value.replace(/[^0-9.-]/g, ''));
                            setFieldValue('geoDataHora', null);
                          }}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            }
                          }}
                          inputProps={{
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>
                      <Mui.Grid item md={4}>
                        <Mui.TextField
                          name="geoLongitude"
                          label={'Longitude'}
                          value={values?.geoLongitude ?? ''}
                          error={Boolean(touched.geoLongitude && errors.geoLongitude)}
                          helperText={touched.geoLongitude && errors.geoLongitude}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={e => {
                            setFieldValue('geoLongitude', e.target.value.replace(/[^0-9.-]/g, ''));
                            setFieldValue('geoDataHora', null);
                          }}
                          variant="outlined"
                          disabled={loadingSaving || loadingCep || loadingCidade}
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                              fontWeight: 'bold',
                            }
                          }}
                          inputProps={{
                            autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                            style: {
                              color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                            },
                          }}
                        />
                      </Mui.Grid>
                      <Mui.Grid item md={4}>
                        <Mui.Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%">
                          <Mui.Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            style={{ height: 56 }}
                            disabled={obtendoLocalizacao || loadingSaving}
                            onClick={() => setShowModalMapa(true)}
                          >
                            {getLabelBtnAbrirMapa(values)}
                          </Mui.Button>
                          {values?.geoDataHora && (
                            <Mui.Typography variant="caption" style={{ marginTop: 4, textAlign: 'center' }}>
                              {`Confirmado em ${new Date(values.geoDataHora).toLocaleDateString(
                                'pt-BR'
                              )} ${new Date(values.geoDataHora).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}`}
                            </Mui.Typography>
                          )}
                        </Mui.Box>
                      </Mui.Grid>
                  </Mui.Grid>

                  {showModalMapa && (
                    <ModalMapaEndereco
                      showModal={showModalMapa}
                      endereco={values}
                      latitude={values?.geoLatitude}
                      longitude={values?.geoLongitude}
                      localizacaoAtual={localizacaoAtual}
                      onClose={() => setShowModalMapa(false)}
                      onConfirm={coords => {
                        setFieldValue('geoLatitude', coords.geoLatitude);
                        setFieldValue('geoLongitude', coords.geoLongitude);
                        setFieldValue('geoOrigem', coords.geoOrigem);
                        setFieldValue('geoDataHora', coords.geoDataHora);
                        setFieldValue('geoUsuarioId', coords.geoUsuarioId);
                        setShowModalMapa(false);
                      }}
                    />
                  )}

                  <Mui.Divider style={{ marginTop: 16, marginBottom: 16 }} />
                  <Mui.Grid container item direction="row" justify="flex-end" alignItems="center">
                    <Mui.Box p={1}>
                      <Mui.Button
                        type="submit"
                        variant="contained"
                        size="large"
                        color="primary"
                        disabled={loadingSaving}
                        autoFocus
                      >
                        {loadingSaving ? (
                          <>
                            <span style={{ paddingLeft: 8 }}>{'Salvando ...'}</span>
                          </>
                        ) : (
                          <>
                            <MuiIcons.CheckCircle />
                            <span style={{ paddingLeft: 8 }}>{'Confirmar'}</span>
                          </>
                        )}
                      </Mui.Button>

                      <Mui.Button
                        size="large"
                        startIcon={<MuiIcons.Close />}
                        disabled={loadingSaving}
                        onClick={onClose}
                        style={{ marginLeft: 16 }}
                      >
                        Cancelar (ESC)
                      </Mui.Button>
                    </Mui.Box>
                  </Mui.Grid>
                </form>
              </>
              );
            }}
          </Formik>
        </Mui.DialogContent>
      </Mui.Dialog>
    </>
  );
};

const ModalListaEnderecoCliente = () => {
  const dispatch = useDispatch();
  const { venda, showModalEnderecoCliente } = useSelector(state => state.pdv);

  const [showModalCadastrarEndereco, setShowModalCadastrarEndereco] = useState(
    showModalEnderecoCliente?.cadastroEndereco
      ? { show: true, data: showModalEnderecoCliente?.data }
      : { show: false }
  );
  const [loading, setLoading] = useState(false);
  const [clienteEnderecos, setClienteEnderecos] = useState([]);

  const handleClose = () => {
    dispatch(actions.setShowModalEnderecoCliente({ show: false }));
  };

  const handleRemoveEndereco = async e => {
    const { isConfirmed } = await toast.confirm(
      'ATENÇÃO',
      'Deseja excluir este registro?</br></br>Certifique-se de que realmente deseja fazer isso, pois esta ação é irreversível.',
      {
        icon: 'question',
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar'
      }
    );

    if (isConfirmed) {
      await api.delete(
        `${API_NODE_URL}/integracao/cliente/${venda?.intCliente?.id}/enderecos/${e?.id}`
      );
      toast.success('Registro removido com sucesso!');
      await loadData();
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${API_NODE_URL}/integracao/cliente/${venda?.intCliente?.id}/enderecos/`
      );

      setClienteEnderecos([...data]);
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      if (venda?.intCliente?.id > 0) {
        await loadData();
      } else {
        let _endereco = null;
        if (showModalEnderecoCliente?.type === 'endereco') {
          _endereco = {
            id: venda?.intClienteEnderecoId,
            descricao: venda?.enderecoDescricao,
            logradouro: venda?.enderecoLogradouro,
            logradouroNumero: venda?.enderecoLogradouroNumero,
            bairro: venda?.enderecoBairro,
            cep: venda?.enderecoCep,
            intCidade: venda?.enderecoIntCidade,
            intCidadeId: venda?.enderecoIntCidade?.id,
            uf: venda?.enderecoUf,
            referencia: venda?.enderecoComplemento,
            geoLatitude: venda?.enderecoGeoLatitude,
            geoLongitude: venda?.enderecoGeoLongitude,
            geoOrigem: venda?.enderecoGeoOrigem,
            geoDataHora: venda?.enderecoGeoDataHora,
            geoUsuarioId: venda?.enderecoGeoUsuarioId
          };
        } else {
          _endereco = {
            id: venda?.entregaIntClienteEnderecoId,
            descricao: venda?.entregaDescricao,
            logradouro: venda?.entregaLogradouro,
            logradouroNumero: venda?.entregaLogradouroNumero,
            bairro: venda?.entregaBairro,
            cep: venda?.entregaCep,
            intCidade: venda?.entregaIntCidade,
            intCidadeId: venda?.entregaIntCidade?.id,
            uf: venda?.entregaUf,
            referencia: venda?.entregaComplemento,
            geoLatitude: venda?.entregaGeoLatitude,
            geoLongitude: venda?.entregaGeoLongitude,
            geoOrigem: venda?.entregaGeoOrigem,
            geoDataHora: venda?.entregaGeoDataHora,
            geoUsuarioId: venda?.entregaGeoUsuarioId
          };
        }

        setShowModalCadastrarEndereco({ show: true, data: _endereco });
      }
    };

    run();
  }, [loadData, venda?.intCliente?.id]);

  return (
    <>
      <Mui.Dialog
        open={showModalEnderecoCliente.show}
        onKeyDown={key => {
          if (key.code === 'Escape') {
            handleClose();
          }
        }}
        PaperComponent={Mui.Paper}
        maxWidth={'lg'}
      >
        <Mui.DialogTitle>
          <Mui.AppBar color="primary" position="static">
            <Mui.Toolbar>
              <Mui.Box style={{ minWidth: 180 }}>
                <Mui.Typography variant="h5" style={{ textTransform: 'upperCase' }}>
                  Escolha o endereço
                </Mui.Typography>
              </Mui.Box>
              <Mui.Box flexGrow={1} />

              <Mui.IconButton color="inherit" onClick={handleClose} aria-label="close">
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Toolbar>
          </Mui.AppBar>
        </Mui.DialogTitle>

        <Mui.DialogContent style={{ padding: 16, width: 800 }} dividers>
          <Mui.Box p={4}>
            {loading ? (
              <>
                <Mui.Box display="flex" justifyContent="center" alignItems="center" p={8}>
                  <Mui.CircularProgress variant="indeterminate" color="primary" />
                </Mui.Box>
              </>
            ) : (
              <>
                {clienteEnderecos.length === 0 && <>Nenhum endereço encontrado</>}
                <Mui.List>
                  {clienteEnderecos.map((item, index) => (
                    <>
                      <Mui.ListItem key={index}>
                        <Mui.Grid container>
                          <Mui.Grid item xs={1}>
                            <Mui.Radio
                              name="radio-button-demo"
                              inputProps={{ 'aria-label': 'A' }}
                              checked={
                                showModalEnderecoCliente.type === 'endereco'
                                  ? venda?.intClienteEnderecoId === item?.id
                                  : venda?.entregaIntClienteEnderecoId === item?.id
                              }
                              onChange={() => {
                                dispatch(
                                  showModalEnderecoCliente.type === 'endereco'
                                    ? actions.selecionarEndereco({ ...item })
                                    : actions.selecionarEnderecoEntrega({ ...item })
                                );
                              }}
                            />
                          </Mui.Grid>
                          <Mui.Grid item xs={8}>
                            <Mui.Box px={3}>
                              <Mui.Typography>{item?.descricao}</Mui.Typography>
                              <Mui.Typography style={{ fontSize: 14, width: '270px' }}>
                                {item?.logradouro}
                              </Mui.Typography>
                              <Mui.Typography style={{ fontSize: 14 }}>
                                N. {item?.logradouroNumero},{item?.bairro}
                              </Mui.Typography>
                              <Mui.Typography style={{ fontSize: 14 }}>
                                {item?.intCidade?.cidade}
                                {'-'}
                                {item?.intCidade?.uf} {''} {item?.cep}
                              </Mui.Typography>
                            </Mui.Box>
                          </Mui.Grid>
                          <Mui.Grid item xs={3}>
                            <Mui.IconButton
                              onClick={() => {
                                setShowModalCadastrarEndereco({ show: true, data: item });
                              }}
                            >
                              <MuiIcons.Edit />
                            </Mui.IconButton>
                            <Mui.IconButton
                              onClick={() => {
                                handleRemoveEndereco(item);
                              }}
                            >
                              <MuiIcons.Delete />
                            </Mui.IconButton>
                            <Mui.IconButton
                              onClick={() => {
                                dispatch(
                                  showModalEnderecoCliente.type === 'endereco'
                                    ? actions.selecionarEndereco({ ...item })
                                    : actions.selecionarEnderecoEntrega({ ...item })
                                );
                                handleClose();
                              }}
                            >
                              <MuiIcons.ArrowForward />
                            </Mui.IconButton>
                          </Mui.Grid>
                        </Mui.Grid>
                      </Mui.ListItem>
                      <Mui.Divider />
                    </>
                  ))}
                </Mui.List>
              </>
            )}
          </Mui.Box>
        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Box display="flex" p={2} justifyContent="center">
            <Mui.Button
              variant="contained"
              className="button-success"
              startIcon={<MuiIcons.AddCircle />}
              onClick={() => {
                setShowModalCadastrarEndereco({ show: true, data: { cep: '' } });
              }}
            >
              <span style={{ paddingLeft: 8 }}> Adicionar endereço</span>
            </Mui.Button>
          </Mui.Box>
        </Mui.DialogActions>
      </Mui.Dialog>

      {showModalCadastrarEndereco.show && (
        <ModalCadastroEnderecoCliente
          showModal={showModalCadastrarEndereco.show}
          onClose={() => {
            setShowModalCadastrarEndereco({ show: false });
            if (!venda?.intCliente?.id)
              dispatch(actions.setShowModalEnderecoCliente({ show: false }));
          }}
          data={showModalCadastrarEndereco?.data}
          callback={async values => {
            dispatch(
              showModalEnderecoCliente.type === 'endereco'
                ? actions.selecionarEndereco({ ...values })
                : actions.selecionarEnderecoEntrega({ ...values })
            );

            setShowModalCadastrarEndereco({ show: false });
            if (venda?.intCliente?.id > 0) {
              await loadData();
            } else {
              dispatch(actions.setShowModalEnderecoCliente({ show: false }));
            }
          }}
        />
      )}
    </>
  );
};

export default ModalListaEnderecoCliente;
